import {Component, Input, OnChanges, SimpleChanges, ElementRef, ViewChild, AfterViewChecked, ChangeDetectorRef, OnInit, inject, Output, EventEmitter, NgZone} from '@angular/core';
import {ReportUserGenerated} from "../../../../core/model-store/models/report-user-generated";
import {MediaHelperService} from "../../../../core/media-helper.service";
import {fetchReportImageUrls, thumbnailImageIds, waitForImages} from "../report-images";

@Component({
    selector: 'report-preview-catalogue',
    templateUrl: './report-preview-catalogue.component.html',
    styleUrls: ['./report-preview-catalogue.component.scss'],
    standalone: false
})
export class ReportPreviewCatalogueComponent implements OnInit, OnChanges, AfterViewChecked {
  private readonly cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly mediaService: MediaHelperService = inject(MediaHelperService);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly ngZone: NgZone = inject(NgZone);

  @ViewChild('reportContainer', { static: false }) reportContainer: ElementRef;
  @Input() reportTemplate: ReportUserGenerated;
  @Input() extractedArtifacts: any;
  @Input() viewOptionsCheckboxValues: any;
  @Input() preview: boolean = false;

  /** Emits when every image has loaded or failed, so the PDF can be printed. */
  @Output() imagesReady = new EventEmitter<void>();

  private inputsSet = false;
  private viewInitialized = false;
  private readonly defaultReportViewOptions : { [key: string]: any } = { "vis_ledetekster": true, "høyrejuster_bilder": true, "ledetekst_og_verdi_på_en_linje": true, "vis_seksjonstitler": true};
  imageUrls : Map<string, string> = new Map();
  private latestUrlRequest = 0;

  ngOnInit(): void {
    //If Report View Options is NULL, select default values, if empy string, leave empty
    if (!this.reportTemplate.reportViewOptions) {
      this.reportTemplate.reportViewOptions = this.defaultReportViewOptions;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.reportTemplate || changes.extractedArtifacts) {
      if (changes.extractedArtifacts) {
        this.loadImageUrls().then();
      }

      const previousFieldSections = changes.reportTemplate?.previousValue?.fieldSections;
      const currentFieldSections = changes.reportTemplate?.currentValue?.fieldSections;

      const previousIsSelectedValues = previousFieldSections ? previousFieldSections.map(section => section.isSelected).join(', ') : '';
      const currentIsSelectedValues = currentFieldSections ? currentFieldSections.map(section => section.isSelected).join(', ') : '';

      this.cdRef.detectChanges();

      if (previousIsSelectedValues !== currentIsSelectedValues) {
        this.cdRef.detectChanges();
      }

      this.inputsSet = this.reportTemplate != null && this.extractedArtifacts != null;
    }
  }

  /** The report shows the first image of each object only. */
  private async loadImageUrls() {
    const request = ++this.latestUrlRequest;
    const missingImageIds = thumbnailImageIds(this.extractedArtifacts)
      .filter(imageId => !this.imageUrls.has(imageId));
    const urls = await fetchReportImageUrls(this.mediaService, missingImageIds);
    if (request !== this.latestUrlRequest) {
      return;
    }
    this.imageUrls = new Map([...this.imageUrls, ...urls]);
    this.cdRef.detectChanges();
    // Outside Angular, or every image event checks the whole report again,
    // so rendering time grew with the square of the number of objects.
    await this.ngZone.runOutsideAngular(() => waitForImages(this.host.nativeElement));
    if (request === this.latestUrlRequest) {
      this.ngZone.run(() => this.imagesReady.emit());
    }
  }

  ngAfterViewChecked() {
    if (this.inputsSet && !this.viewInitialized) {
      this.viewInitialized = true;
    }
  }

  isLeftAdjustedImages() {
    return this.viewOptionsCheckboxValues.find((option) => option.key === 'venstrejusterte_bilder').value;
  }

  isHidePrompts() {
    return this.viewOptionsCheckboxValues.find((option) => option.key === 'skjul_ledetekster').value;
  }

  isViewPromptSeparateLines() {
    return this.viewOptionsCheckboxValues.find((option) => option.key === 'ledetekst_og_verdi_på_egne_linjer').value;
  }

  isViewWithFullImage() {
    return this.viewOptionsCheckboxValues.find((option) => option.key === 'med_helbilde').value;
  }

  isShowSectionTitles() {
    return this.viewOptionsCheckboxValues.find((option) => option.key === 'vis_seksjonstitler').value;
  }

}
